import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { LANGUAGE_CHOICES } from '@vigeo/shared';
import { Button, Checkbox, Input, Label, Popover, PopoverSurface, PopoverTrigger, Radio, RadioGroup, Select, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { PillField, placeWords } from '@/features/places';
import { complianceAndDocumentsShown } from '@/lib/sections';
import { readinessBandOrder, statePresenters } from '@/features/readiness';
import {
  COMPLIANCE_NOT_TRACKED_LABEL, PHONE_LABEL, STATUS_LABEL, complianceCount, filtersAreOn, filterTags, nameFilterCount, noRosterFilters, phoneCount,
  statusFilterOn, toggled, withJobTitles, withLanguages, withoutTag,
  type ComplianceChoice, type NameSort, type PhoneChoice, type RosterFilters, type StatusChoice,
} from './rosterFilters.js';
import { NO_AREA } from './staff.js';

/** The Compliance choices, in the order Figma v2-3 draws them: Not tracked yet first, then every readiness state. */
export const COMPLIANCE_CHOICES: readonly ComplianceChoice[] = ['not-tracked', ...readinessBandOrder];
export const complianceLabel = (choice: ComplianceChoice): string => (choice === 'not-tracked' ? COMPLIANCE_NOT_TRACKED_LABEL : statePresenters[choice].label);
export const areaLabel = (area: string): string => (area === NO_AREA ? 'No service area' : area);

/**
 * A filter popover's open state, and the keyboard going back to its button when it closes (PG11: "the keyboard goes back to the button
 * that opened it"). A click does not give a button the keyboard in Safari on a Mac, and Fluent alone did not bring it back there: a
 * click outside left the keyboard on the page, and so did Esc pressed in the list (the review of #542 also saw it land on another button).
 *
 * So when it closes the button is focused if the keyboard is nowhere (a click on plain page) or still in the list (Esc pressed there:
 * the list is still on the page when this runs, and is taken away after). Anywhere else, a click on another field for one, it is
 * left where the person put it.
 */
function useReturningPopover() {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) { wasOpen.current = true; return; }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const keyboard = document.activeElement;
    if (keyboard === null || keyboard === document.body || surface.current?.contains(keyboard) === true) trigger.current?.focus();
  }, [open]);
  const close = useCallback(() => setOpen(false), []);
  return { trigger, surface, close, popover: { open, onOpenChange: (_: unknown, data: { readonly open: boolean }) => setOpen(data.open) } } as const;
}

/** The controls every roster filter surface shares: the filters, how to change them, and the areas in use. */
export interface RosterControls {
  readonly filters: RosterFilters;
  readonly onFilters: (next: RosterFilters) => void;
  readonly sort: NameSort;
  readonly onSort: (next: NameSort) => void;
  /** Typed service areas in use, sorted. */
  readonly areas: readonly string[];
  /** The places held by someone on the roster that are in the app's own list, with the words for each, sorted by those words. Absent: none are offered. */
  readonly places?: readonly { readonly placeId: string; readonly label: string }[];
  /** Whether anyone has no service area, so "No service area" is offered only when it would match someone. */
  readonly anyWithoutArea: boolean;
  /** The job titles held on the roster, with their words (`jobTitleOptions`). Absent: no Job title filter is offered. */
  readonly jobTitles?: readonly { readonly key: string; readonly label: string }[];
}

const useStyles = makeStyles({
  row: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  /**
   * Search and Service area give up width before the row wraps (Julio, `FR one line`, #544), so at a laptop-size window the six
   * controls stay on one line. A wrapping row breaks its lines by each item's STARTING width and only then shares out what is
   * left on the line. So the starting width here is the SMALLEST each box may be, the width it was drawn with is its MAXIMUM, and
   * the two grow into whatever room the line has: with room to spare both are as wide as they always were. The two dropdowns
   * keep their width.
   *
   * Search also needs `minWidth: 0`: without it the browser will not make the column narrower than the text box's own built-in
   * width, which is wider than the starting width, and the row wraps sooner than these numbers say.
   */
  search: { flexGrow: 1, flexShrink: 1, flexBasis: '10rem', minWidth: 0, maxWidth: '15rem' },
  area: { flexGrow: 1, flexShrink: 1, flexBasis: '15rem', maxWidth: '18.75rem' },
  compliance: { width: '12rem' },
  status: { width: '12rem' },
  line: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  tag: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS,
    paddingBlock: tokens.spacingVerticalXXS, paddingInlineStart: tokens.spacingHorizontalS, paddingInlineEnd: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, color: tokens.colorNeutralForeground1,
  },
  tagX: { minWidth: 'auto', padding: tokens.spacingHorizontalXXS },
  menu: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, width: '18.75rem', padding: tokens.spacingHorizontalS },
  menuTitle: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold },
  menuFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, paddingTop: tokens.spacingVerticalS, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  pickerField: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  /**
   * The buttons at the end of the row (Saved filters). A column like every field: a hidden label row (the same height as
   * the others), then ONE line of controls, so their top and bottom are the controls' top and bottom whatever the Service area box
   * holds. The column wraps as a whole. On a phone it is a full-width line and the empty label row is dropped.
   */
  trailing: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, [shellMedia.phone]: { width: '100%' } },
  trailingGap: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, visibility: 'hidden', [shellMedia.phone]: { display: 'none' } },
  trailingLine: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  control: { minHeight: shellMetrics.controlHeight },
  /**
   * Search is a Fluent Input, and the theme gives every Input a minimum height (`fieldHeight`; the touch size on a phone) with a
   * rule a class's own minimum cannot beat. So this sets the HEIGHT, which the theme leaves alone: the box is as tall as the
   * controls beside it, and on a phone the theme's larger minimum still wins. (An inline minimum would win on a phone too, and
   * the box would be under the touch size there.)
   */
  searchBox: { height: shellMetrics.controlHeight },
  picker: { width: '100%', minHeight: shellMetrics.controlHeight, justifyContent: 'space-between', fontWeight: tokens.fontWeightRegular },
  pickerText: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  headBtn: { minWidth: 'auto', whiteSpace: 'nowrap', paddingInline: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground2 },
  headOn: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1 },
  headOnText: { color: tokens.colorBrandForeground1 },
  headCell: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS },
  clear: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  noWrap: { whiteSpace: 'nowrap' },
  suggest: {
    listStyleType: 'none', margin: 0, padding: 0, position: 'absolute', insetInline: 0, insetBlockStart: '100%', zIndex: 2,
    backgroundColor: tokens.colorNeutralBackground1, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, boxShadow: tokens.shadow16,
    maxHeight: '14rem', overflowY: 'auto',
  },
  suggestOption: { paddingBlock: tokens.spacingVerticalS, paddingInline: tokens.spacingHorizontalM, cursor: 'pointer', color: tokens.colorNeutralForeground1 },
  suggestActive: { backgroundColor: tokens.colorNeutralBackground1Hover },
  suggestNone: { paddingBlock: tokens.spacingVerticalS, paddingInline: tokens.spacingHorizontalM, color: tokens.colorNeutralForeground3 },
  headLabel: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  // THE COMPACT FORM (#689; Figma page 12, frames M1 and T3): Search and one Filters button on one line. Search takes the room.
  compact: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
  compactSearch: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 },
  compactButton: { flexShrink: 0, minHeight: shellMetrics.controlHeight, whiteSpace: 'nowrap' },
  compactOn: { color: tokens.colorBrandForeground1 },
  // What the Filters button opens: the same fields as the row, one under the other.
  compactMenu: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, width: '18.75rem', maxWidth: '100%', boxSizing: 'border-box', padding: tokens.spacingHorizontalS },
  compactField: { width: '100%' },
  /**
   * THE PLACE BOX INSIDE THE FILTERS PANEL RUNS ONTO FURTHER LINES (reviewer 223 on #691; Carlos's #686). In the filter row the box
   * shares a line with three other controls, and #686 keeps it to one line there by letting a long name shrink and be cut. In the
   * panel it is on a line of its own, 284 wide: kept to one line, three chosen places read "Fr…", "A..", "M". So in here,
   * whatever the box itself was told, its chosen places wrap and every name is whole.
   *
   * Written as rules on what is INSIDE this wrapper (the wrapper, the field's root, its box, the list of chosen places, a
   * place, its words), so the row's box is not touched, and nothing here names anything #686 adds: it holds whichever of the
   * two is merged first. e2e/roster-map-view.spec.ts measures three chosen places in the panel.
   */
  compactPlace: {
    '& > div > div': { flexWrap: 'wrap' },
    '& > div > div > ul > li': { minWidth: 'auto', flexShrink: 0 },
    '& > div > div > ul > li > span': { overflow: 'visible', textOverflow: 'clip', whiteSpace: 'normal' },
  },
  // A filter the panel has no field for (reviewer 223 on #691, finding 6): said in the panel, with its own way off.
  compactCarried: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  compactFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: tokens.spacingHorizontalM, paddingTop: tokens.spacingVerticalS, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
});

/**
 * A filter-row field that picks several values: a button that looks like the other fields and opens
 * the same tick list as the column header (Fluent's multiselect Dropdown would do the same job, but it
 * cannot be driven in a unit test). The button says what is chosen, or what "nothing chosen" means.
 */
function PickerField({ label, empty, chosen, className, children }: { readonly label: string; readonly empty: string; readonly chosen: readonly string[]; readonly className: string; readonly children: ReactNode }) {
  const styles = useStyles();
  const text = chosen.length === 0 ? empty : chosen.join(', ');
  const { trigger, surface, popover } = useReturningPopover();
  // Not a Fluent `Field`: a Field hands its label to every control inside it, popover content included,
  // and every tick box in the list would then be named after the field.
  return (
    <div className={mergeClasses(styles.pickerField, className)}>
    <Label weight="semibold">{label}</Label>
    <Popover {...popover} positioning="below-start" trapFocus={false}>
      <PopoverTrigger disableButtonEnhancement>
        <Button ref={trigger} appearance="outline" className={styles.picker} iconPosition="after" icon={<ShellIcons.chevronDown />} aria-label={`${label}: ${text}`}>
          <span className={styles.pickerText}>{text}</span>
        </Button>
      </PopoverTrigger>
      <PopoverSurface ref={surface}>
        <div className={styles.menu}>{children}</div>
      </PopoverSurface>
    </Popover>
    </div>
  );
}

/**
 * The Place filter in the row (Figma "Concept A v2" frames 1 and 2; called "Place" since #563): the same pill field as the
 * picker, but it searches what is ON THE ROSTER (places held by someone, text typed before places existed, and "No service
 * area"), here, with no request to Google. A place chosen here is the same selection the map's pins and the places list show.
 */
function ServiceAreaField({ controls, areaOptions }: { readonly controls: RosterControls; readonly areaOptions: readonly string[] }) {
  const styles = useStyles();
  const ids = useId();
  const { filters, onFilters } = controls;
  const places = controls.places ?? [];
  const chosenPlaces = filters.places ?? [];
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  type Option = { readonly key: string; readonly label: string; readonly add: () => void };
  const all: Option[] = [
    ...areaOptions.filter((area) => !filters.areas.includes(area)).map((area): Option => ({ key: `area:${area}`, label: areaLabel(area), add: () => onFilters({ ...filters, areas: [...filters.areas, area] }) })),
    ...places.filter((place) => !chosenPlaces.includes(place.placeId)).map((place): Option => ({ key: `place:${place.placeId}`, label: place.label, add: () => onFilters({ ...filters, places: [...chosenPlaces, place.placeId] }) })),
  ];
  const needle = text.trim().toLowerCase();
  const shown = open ? all.filter((option) => needle === '' || option.label.toLowerCase().includes(needle)) : [];
  const pills = [
    ...filters.areas.map((area) => ({ key: `area:${area}`, label: areaLabel(area), onRemove: () => onFilters({ ...filters, areas: filters.areas.filter((a) => a !== area) }) })),
    ...chosenPlaces.map((id) => ({ key: `place:${id}`, label: placeWords(id), onRemove: () => onFilters({ ...filters, places: chosenPlaces.filter((p) => p !== id) }) })),
  ];
  // The list closes on a pick (the box keeps focus, so nothing else would close it): left open it covers the tag line under the row.
  const pick = (option: Option) => { option.add(); setText(''); setActive(-1); setOpen(false); };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) { setOpen(true); return; }
      if (shown.length === 0) return;
      setActive((current) => (event.key === 'ArrowDown' ? (current + 1) % shown.length : (current <= 0 ? shown.length - 1 : current - 1)));
    } else if (event.key === 'Enter') {
      const target = shown[active];
      if (target !== undefined) { event.preventDefault(); pick(target); }
    } else if (event.key === 'Escape' && open) {
      event.preventDefault(); setOpen(false); setActive(-1);
    }
  };
  return (
    <PillField
      // This box shares a line with Search and the two dropdowns, so it must not grow (#669).
      oneLine
      pillsLabel="Chosen places"
      removeLabel={(label) => `Remove place ${label}`}
      pills={pills}
      input={{
        id: 'roster-area',
        value: text,
        placeholder: pills.length === 0 ? 'City or ZIP code' : 'Add another',
        autoComplete: 'off',
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-expanded': open,
        'aria-controls': `${ids}-list`,
        'aria-activedescendant': open && active >= 0 ? `${ids}-opt-${active}` : undefined,
        onChange: (event) => { setText(event.target.value); setOpen(true); setActive(-1); },
        onFocus: () => { if (text.trim() !== '') setOpen(true); },
        onKeyDown,
        onBlur: () => setOpen(false),
      }}
    >
      {open ? (
        <ul id={`${ids}-list`} role="listbox" aria-label="Places on the roster" className={styles.suggest}>
          {shown.length === 0 ? <li role="presentation" className={styles.suggestNone}>No place on the roster matches.</li> : null}
          {shown.map((option, i) => (
            <li
              key={option.key}
              id={`${ids}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className={mergeClasses(styles.suggestOption, i === active && styles.suggestActive)}
              onMouseDown={(event) => { event.preventDefault(); pick(option); }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      ) : null}
    </PillField>
  );
}

/** Place, and Active or inactive, as the filter row and the compact form both draw them: one copy of each field. */
function PlaceFilter({ controls }: { readonly controls: RosterControls }) {
  const { areas, anyWithoutArea } = controls;
  return (
    <>
      <Label htmlFor="roster-area" weight="semibold">Place</Label>
      <ServiceAreaField controls={controls} areaOptions={anyWithoutArea ? [...areas, NO_AREA] : areas} />
    </>
  );
}
/** Compliance as a field: a button that says what is chosen and opens the tick list. Inside the Filters button it is a list inside a list, so the six ticks do not make that one taller than a phone's screen. */
function CompliancePicker({ controls, className }: { readonly controls: RosterControls; readonly className: string }) {
  const { filters, onFilters } = controls;
  return (
    <PickerField label="Compliance" empty="All statuses" chosen={filters.compliance.map(complianceLabel)} className={className}>
      {COMPLIANCE_CHOICES.map((choice) => (
        <Checkbox key={choice} label={complianceLabel(choice)} checked={filters.compliance.includes(choice)} onChange={(_, data) => onFilters({ ...filters, compliance: toggled(filters.compliance, choice, data.checked === true) })} />
      ))}
    </PickerField>
  );
}
/** The tick list of the closed language list, as the Filters panel and the Languages column heading both draw it. */
function LanguageChecks({ controls }: { readonly controls: RosterControls }) {
  const { filters, onFilters } = controls;
  const chosen = filters.languages ?? [];
  return (
    <>
      {LANGUAGE_CHOICES.map((choice) => (
        // Left out when empty, not set to an empty list, so "Clear all filters" and a never-touched filter compare equal.
        <Checkbox key={choice.id} label={choice.label} checked={chosen.includes(choice.id)} onChange={(_, data) => onFilters(withLanguages(filters, toggled(chosen, choice.id, data.checked === true)))} />
      ))}
    </>
  );
}

/**
 * Language as a field (#772): a button that says what is chosen and opens the tick list of the closed language list.
 *
 * IN THE FILTERS PANEL, NOT IN THE ROW. A sixth control does not fit the row's one line at 1440 with a place chosen (measured with
 * e2e/roster-filter-row.spec.ts: Saved filters wraps under the others), which is Julio's `FR one line` (#544). On the desktop Table
 * the filter is in the Languages column's heading (Julio chose that, #772: "ok proceed column"); this panel is for the Map view and phones.
 * The column, and so its filter, is drawn at every width the desktop table is (#798).
 */
function LanguagePicker({ controls, className }: { readonly controls: RosterControls; readonly className: string }) {
  const chosen = controls.filters.languages ?? [];
  return (
    <PickerField label="Language" empty="Any language" chosen={LANGUAGE_CHOICES.filter((choice) => chosen.includes(choice.id)).map((choice) => choice.label)} className={className}>
      <LanguageChecks controls={controls} />
    </PickerField>
  );
}
/**
 * Job title as a field (#804): coverage read per discipline. The map's counts follow it ("when we switch to PT, maybe the map
 * shrinks"). Only the titles somebody on the roster holds are offered. In the Filters panel, for the reason Language is (above).
 */
function JobTitlePicker({ controls, className }: { readonly controls: RosterControls; readonly className: string }) {
  const { filters, onFilters } = controls;
  const options = controls.jobTitles ?? [];
  const chosen = filters.jobTitles ?? [];
  if (options.length === 0 && chosen.length === 0) return null;
  return (
    <PickerField label="Job title" empty="Any job title" chosen={options.filter((option) => chosen.includes(option.key)).map((option) => option.label)} className={className}>
      {options.map((option) => (
        <Checkbox key={option.key} label={option.label} checked={chosen.includes(option.key)} onChange={(_, data) => onFilters(withJobTitles(filters, toggled(chosen, option.key, data.checked === true)))} />
      ))}
    </PickerField>
  );
}

function StatusFilter({ controls }: { readonly controls: RosterControls }) {
  const styles = useStyles();
  const { filters, onFilters } = controls;
  const status = statusFilterOn(filters) ? filters.statuses[0]! : 'all';
  return (
    <>
      <Label htmlFor="roster-status" weight="semibold">Active or inactive</Label>
      <Select id="roster-status" className={styles.control} select={{ className: styles.control }} value={status} onChange={(_, data) => onFilters({ ...filters, statuses: data.value === 'all' ? [] : [data.value as StatusChoice] })}>
        <option value="all">Active and inactive</option>
        <option value="active">Active only</option>
        <option value="inactive">Inactive only</option>
      </Select>
    </>
  );
}

export const COMPACT_FILTERS = 'Filters';
/** What the Filters panel hands a control inside it. */
export interface CompactFiltersPanel {
  /** Closes the panel and, once it has left the page and the keyboard is back on the Filters button, runs `after`. */
  readonly closeThen: (after: () => void) => void;
}
export const COMPACT_SEARCH_PLACEHOLDER = 'Search staff';
/** What the panel calls a filter that is on and has no field in it. */
export const COMPACT_CARRIED = 'Also on';

/**
 * THE COMPACT FORM OF THE FILTER ROW (#689; Figma page 12, frames M1 and T3): Search, and one Filters button that opens Place,
 * Compliance, Active or inactive and (for someone who may edit) Saved filters. It is the top of the roster side in the Map
 * view, and the Table view's filters on a phone, where four stacked fields pushed the staff off the first screen.
 *
 * It reads and writes the SAME one filter value as the row and the column headers, so nothing is carried between them. The
 * button says how many choices are on ("Filters · 2"; the search box is not counted, it is beside the button), because with
 * the fields folded away nothing else on the roster side would.
 */
export function RosterCompactFilters({ controls, trailing, buttonRef, searchPlaceholder = COMPACT_SEARCH_PLACEHOLDER, className }: {
  readonly controls: RosterControls;
  /** What ends the panel, beside Clear all filters. A function is handed the panel, for a control in it that opens something of its own. */
  readonly trailing?: ReactNode | ((panel: CompactFiltersPanel) => ReactNode);
  /** The page's own hold on the Filters button, so a dialog opened from inside the panel can give the keyboard back to it. */
  readonly buttonRef?: RefObject<HTMLButtonElement | null>;
  readonly searchPlaceholder?: string;
  readonly className?: string;
}) {
  const styles = useStyles();
  const { filters, onFilters } = controls;
  const { trigger, surface, close, popover } = useReturningPopover();
  const setButton = useCallback((button: HTMLButtonElement | null) => {
    trigger.current = button;
    if (buttonRef !== undefined) buttonRef.current = button;
  }, [trigger, buttonRef]);
  /**
   * Close the panel, THEN do `after` (reviewer 223, #691 finding 2; the same order as the Saved filters list, PG12: never two
   * pop-ups at once). "Closed" is the moment the panel has left the page, not the moment its state flips, so this looks until
   * it has. The keyboard is put on the Filters button before `after` runs: whatever opens next finds it there and can hand it
   * back there, and the control that asked, which was inside the panel, is gone by then.
   */
  const afterClose = useRef<(() => void) | null>(null);
  const closeThen = useCallback((after: () => void) => { afterClose.current = after; close(); }, [close]);
  const panelOpen = popover.open;
  useEffect(() => {
    if (panelOpen || afterClose.current === null) return undefined;
    const after = afterClose.current;
    afterClose.current = null;
    let timer: ReturnType<typeof setTimeout>;
    let tries = 0;
    const whenGone = () => {
      tries += 1;
      if (surface.current === null || !surface.current.isConnected || tries > 40) { trigger.current?.focus(); after(); return; }
      timer = setTimeout(whenGone, 25);
    };
    whenGone();
    return () => clearTimeout(timer);
  }, [panelOpen, surface, trigger]);
  const tags = filterTags(filters, complianceLabel, placeWords).filter((tag) => tag.key !== 'query');
  const on = tags.length;
  const text = on === 0 ? COMPACT_FILTERS : `${COMPACT_FILTERS} · ${on}`;
  /**
   * THE PHONE FILTER HAS NO FIELD IN THE PANEL (reviewer 223 on #691, finding 6). It belongs to the Table's Phone column, and the
   * panel is drawn with three fields (frames M1 and T3), so none is added for it. But the button counts it ("Filters · 1"), and
   * in the Map view nothing else could take it off but "Clear all filters". So a phone filter that is on is said here, as the
   * tag the Table's own line shows, with the same x.
   */
  const carried = tags.filter((tag) => tag.key.startsWith('phone:'));
  return (
    <div className={mergeClasses(styles.compact, className)} role="search" aria-label="Filter the roster">
      <Input
        id="roster-search"
        className={mergeClasses(styles.searchBox, styles.compactSearch)}
        type="search"
        aria-label="Search"
        placeholder={searchPlaceholder}
        contentAfter={<ShellIcons.search />}
        value={filters.query}
        onChange={(_, data) => onFilters({ ...filters, query: data.value })}
      />
      <Popover {...popover} positioning="below-end" trapFocus={false}>
        <PopoverTrigger disableButtonEnhancement>
          <Button ref={setButton} appearance="outline" className={mergeClasses(styles.compactButton, on > 0 && styles.compactOn)} iconPosition="after" icon={<ShellIcons.chevronDown />}>{text}</Button>
        </PopoverTrigger>
        <PopoverSurface ref={surface}>
          <div className={styles.compactMenu} role="group" aria-label={COMPACT_FILTERS}>
            <div className={mergeClasses(styles.pickerField, styles.compactPlace)} data-compact-place=""><PlaceFilter controls={controls} /></div>
            <JobTitlePicker controls={controls} className={styles.compactField} />
            <LanguagePicker controls={controls} className={styles.compactField} />
            {complianceAndDocumentsShown() ? <CompliancePicker controls={controls} className={styles.compactField} /> : null}
            <div className={styles.pickerField}><StatusFilter controls={controls} /></div>
            {carried.length === 0 ? null : (
              <div className={styles.compactCarried} role="group" aria-label={COMPACT_CARRIED}>
                <span className={styles.menuTitle}>{COMPACT_CARRIED}</span>
                <div className={styles.line}>
                  {carried.map((tag) => (
                    <span key={tag.key} className={styles.tag}>
                      {tag.label}
                      <Button className={styles.tagX} size="small" appearance="transparent" icon={<ShellIcons.close />} aria-label={`Remove filter ${tag.label}`} onClick={() => onFilters(withoutTag(filters, tag.key))} />
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className={styles.compactFoot}>
              <Button appearance="transparent" className={styles.clear} disabled={!filtersAreOn(filters)} onClick={() => onFilters(noRosterFilters)}>Clear all filters</Button>
              {typeof trailing === 'function' ? trailing({ closeThen }) : trailing ?? null}
            </div>
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}

/** The filter row: Search, Place, Compliance, Active or inactive. Labels sit above the fields (the 00a rule). */
export function RosterFilterRow({ controls, trailing }: { readonly controls: RosterControls; readonly trailing?: ReactNode }) {
  const styles = useStyles();
  const { filters, onFilters } = controls;
  return (
    <div className={styles.row} role="search" aria-label="Filter the roster">
      <div className={mergeClasses(styles.pickerField, styles.search)}>
        <Label htmlFor="roster-search" weight="semibold">Search</Label>
        <Input
          id="roster-search"
          className={styles.searchBox}
          type="search"
          placeholder="Name, email, phone or place"
          contentAfter={<ShellIcons.search />}
          value={filters.query}
          onChange={(_, data) => onFilters({ ...filters, query: data.value })}
        />
      </div>
      <div className={mergeClasses(styles.pickerField, styles.area)}><PlaceFilter controls={controls} /></div>
      {complianceAndDocumentsShown() ? <CompliancePicker controls={controls} className={styles.compliance} /> : null}
      <div className={mergeClasses(styles.pickerField, styles.status)}><StatusFilter controls={controls} /></div>
      {trailing === undefined || trailing === null ? null : (
        <div className={styles.trailing}>
          <span className={styles.trailingGap} aria-hidden="true">&nbsp;</span>
          <div className={styles.trailingLine}>{trailing}</div>
        </div>
      )}
    </div>
  );
}

/** "Showing N of M staff", a tag per active filter with an x, and Clear all filters. With no filter on it says "Showing all M staff" and nothing else (frame A1, #563). */
export function ActiveFilters({ controls, shown, total, trailing }: { readonly controls: RosterControls; readonly shown: number; readonly total: number; readonly trailing?: ReactNode }) {
  const styles = useStyles();
  const { filters, onFilters } = controls;
  if (!filtersAreOn(filters)) return <section className={styles.line} aria-label="Active filters"><span>Showing all {total} staff</span></section>;
  return (
    <section className={styles.line} aria-label="Active filters">
      <span>Showing {shown} of {total} staff</span>
      {filterTags(filters, complianceLabel, placeWords).map((tag) => (
        <span key={tag.key} className={styles.tag}>
          {tag.label}
          <Button className={styles.tagX} size="small" appearance="transparent" icon={<ShellIcons.close />} aria-label={`Remove filter ${tag.label}`} onClick={() => onFilters(withoutTag(filters, tag.key))} />
        </span>
      ))}
      <Button appearance="transparent" className={styles.clear} onClick={() => onFilters(noRosterFilters)}>Clear all filters</Button>
      {trailing}
    </section>
  );
}

interface MenuProps {
  /** The filter's name, which is also the header control's name. */
  readonly label: string;
  /** What the column is called on screen, when the column holds more than the filter is about ("Phone and email"). Absent: `label`. */
  readonly column?: string;
  readonly count: number;
  readonly title?: string;
  readonly foot: string;
  readonly onClear: () => void;
  readonly children: ReactNode;
  /**
   * Name only: its sort arrow and its filter arrow are separate controls in the same header, so the
   * label is plain text, `between` is the sort arrow, and the filter arrow alone opens the menu.
   */
  readonly between?: ReactNode;
}

/**
 * A column header with a filter menu. A header with a filter on says so, in the brand colour with its
 * count ("Compliance · 1"). Ticking applies at once; there is no Apply button.
 */
function HeaderFilter({ label, column, count, title, foot, onClear, children, between }: MenuProps) {
  const styles = useStyles();
  const on = count > 0;
  const text = on ? `${column ?? label} · ${count}` : column ?? label;
  const split = between !== undefined;
  const { trigger, surface, popover } = useReturningPopover();
  const menu = (
    <PopoverSurface ref={surface}>
      <div className={styles.menu}>
        {title === undefined ? null : <span className={styles.menuTitle}>{title}</span>}
        {children}
        <div className={styles.menuFoot}>
          <span>{foot}</span>
          <Button appearance="transparent" size="small" className={mergeClasses(styles.noWrap, styles.clear)} onClick={onClear}>Clear this filter</Button>
        </div>
      </div>
    </PopoverSurface>
  );
  if (split) {
    return (
      <span className={styles.headCell}>
        <span className={mergeClasses(styles.headLabel, on && styles.headOnText)}>{text}</span>
        {between}
        <Popover {...popover} positioning="below-start" trapFocus={false}>
          <PopoverTrigger disableButtonEnhancement>
            <Button ref={trigger} size="small" appearance="transparent" className={styles.headBtn} aria-label={`Filter ${label.toLowerCase()}`} icon={<ShellIcons.chevronDown />} />
          </PopoverTrigger>
          {menu}
        </Popover>
      </span>
    );
  }
  return (
    <Popover {...popover} positioning="below-end" trapFocus={false}>
      <PopoverTrigger disableButtonEnhancement>
        <Button ref={trigger} size="small" appearance="transparent" className={mergeClasses(styles.headBtn, on && styles.headOn)} iconPosition="after" icon={<ShellIcons.chevronDown />}>
          {text}
        </Button>
      </PopoverTrigger>
      {menu}
    </Popover>
  );
}

export function NameHeader({ controls }: { readonly controls: RosterControls }) {
  const styles = useStyles();
  const { filters, onFilters, sort, onSort } = controls;
  const flipped: NameSort = sort === 'asc' ? 'desc' : 'asc';
  return (
    <HeaderFilter
      label="Name"
      count={nameFilterCount(controls.filters)}
      foot="Same filter as Active or inactive above."
      onClear={() => onFilters({ ...filters, statuses: [] })}
      between={
        <Button size="small" appearance="transparent" className={styles.headBtn} aria-label={`Sort names ${flipped === 'asc' ? 'A to Z' : 'Z to A'}`} icon={sort === 'asc' ? <ShellIcons.chevronUp /> : <ShellIcons.chevronDown />} onClick={() => onSort(flipped)} />
      }
    >
      <RadioGroup value={sort} onChange={(_, data) => onSort(data.value as NameSort)} aria-label="Sort names">
        <Radio value="asc" label="Sort A to Z" />
        <Radio value="desc" label="Sort Z to A" />
      </RadioGroup>
      {(['active', 'inactive'] as const).map((choice) => (
        <Checkbox key={choice} label={STATUS_LABEL[choice]} checked={filters.statuses.includes(choice)} onChange={(_, data) => onFilters({ ...filters, statuses: toggled(filters.statuses, choice, data.checked === true) })} />
      ))}
    </HeaderFilter>
  );
}

/** `column` is what the header says: "Phone and email" while they share a column, "Phone" once Email has a column of its own (#673). */
export function PhoneHeader({ controls, column = 'Phone and email' }: { readonly controls: RosterControls; readonly column?: string }) {
  const { filters, onFilters } = controls;
  return (
    <HeaderFilter label="Phone" column={column} count={phoneCount(filters)} foot="Both or neither means no phone filter." onClear={() => onFilters({ ...filters, phones: [] })}>
      {(['has', 'none'] as const).map((choice: PhoneChoice) => (
        <Checkbox key={choice} label={PHONE_LABEL[choice]} checked={filters.phones.includes(choice)} onChange={(_, data) => onFilters({ ...filters, phones: toggled(filters.phones, choice, data.checked === true) })} />
      ))}
    </HeaderFilter>
  );
}

/** The Languages column's heading with its filter, like Phone's: ticking applies at once, and it is the same choice the Filters panel holds (#772). */
export function LanguageHeader({ controls }: { readonly controls: RosterControls }) {
  const { filters, onFilters } = controls;
  return (
    <HeaderFilter label="Language" column="Languages" count={(filters.languages ?? []).length} title="Show staff who speak" foot="Any ticked language. Same choice as Filters." onClear={() => onFilters(withLanguages(filters, []))}>
      <LanguageChecks controls={controls} />
    </HeaderFilter>
  );
}

export function ComplianceHeader({ controls }: { readonly controls: RosterControls }) {
  const { filters, onFilters } = controls;
  return (
    <HeaderFilter label="Compliance" count={complianceCount(filters)} title="Show staff whose compliance is" foot="Same filter as Compliance above." onClear={() => onFilters({ ...filters, compliance: [] })}>
      {COMPLIANCE_CHOICES.map((choice) => (
        <Checkbox key={choice} label={complianceLabel(choice)} checked={filters.compliance.includes(choice)} onChange={(_, data) => onFilters({ ...filters, compliance: toggled(filters.compliance, choice, data.checked === true) })} />
      ))}
    </HeaderFilter>
  );
}
